import config from "../config";
import { get as getLocalStorage } from "../services/localStorage";
import { sanitizeImageUrl } from "./sanitizeImageUrl";

export function getStoredAuth() {
  try {
    return getLocalStorage({ key: "auth" }) || {};
  } catch (e) {
    return {};
  }
}

export function getAuthUserId(auth) {
  const source = auth?.token ? auth : getStoredAuth();
  const user = source.user || {};
  const id =
    source.id ||
    user.id ||
    source.user_id ||
    user.user_id ||
    source.pk ||
    user.pk ||
    "";
  return id === 0 || id ? String(id) : "";
}

export function getAuthDisplayName(auth) {
  const source = auth?.token ? auth : getStoredAuth();
  const user = source.user || {};
  const first =
    source.firstname ||
    source.first_name ||
    user.firstname ||
    user.first_name ||
    "";
  const last =
    source.lastname ||
    source.last_name ||
    user.lastname ||
    user.last_name ||
    "";
  const full = [first, last].filter(Boolean).join(" ").trim();
  return (
    full ||
    source.username ||
    user.username ||
    source.email ||
    user.email ||
    "Account"
  );
}

export function getAuthInitials(auth) {
  const name = getAuthDisplayName(auth);
  if (!name || name === "Account") return "U";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

export function resolveMediaUrl(img) {
  if (img == null || img === "") return "";
  if (typeof img === "object") {
    return resolveMediaUrl(
      img.url ||
        img.image ||
        img.image_url ||
        img.src ||
        img.path ||
        ""
    );
  }

  const cleaned = sanitizeImageUrl(img);
  if (!cleaned) return "";

  const lowered = cleaned.toLowerCase();
  if (
    lowered === "null" ||
    lowered === "undefined" ||
    lowered === "none" ||
    lowered === "n/a" ||
    lowered === "-"
  ) {
    return "";
  }

  if (
    cleaned.startsWith("http://") ||
    cleaned.startsWith("https://") ||
    cleaned.startsWith("data:") ||
    cleaned.startsWith("blob:")
  ) {
    return cleaned;
  }

  const base = (config.baseUrl || "").replace(/\/$/, "");
  if (!base) return cleaned;
  return `${base}/${cleaned.replace(/^\/*/, "")}`;
}

export function pickUserImage(...sources) {
  for (let i = 0; i < sources.length; i += 1) {
    const source = sources[i];
    if (!source || typeof source !== "object") continue;
    const value =
      source.image ||
      source.image_url ||
      source.profile_image ||
      source.profile_image_url ||
      source.avatar ||
      source.avatar_url ||
      "";
    const resolved = resolveMediaUrl(value);
    if (resolved) return resolved;
  }
  return "";
}

export function getAuthAvatarUrl(auth) {
  const source = auth?.token ? auth : getStoredAuth();
  const user = source.user || {};
  return pickUserImage(source, user);
}

/**
 * Patch for UPDATE_AUTH after profile save/upload so header/menu stay in sync.
 * Accepts form fields (first_name/last_name) and optional image path.
 */
export function buildAuthProfilePatch(form = {}, imagePath) {
  const first =
    form.first_name ||
    form.firstname ||
    form.firstName ||
    "";
  const last =
    form.last_name ||
    form.lastname ||
    form.lastName ||
    "";
  const patch = {
    firstname: first,
    lastname: last,
    first_name: first,
    last_name: last,
  };
  if (imagePath) {
    patch.image = imagePath;
  }
  return patch;
}

export function withCacheBust(url) {
  if (!url) return "";
  // Don't cache-bust absolute S3/CDN URLs with existing signed query strings.
  if (/[?&](X-Amz-|Signature=|Expires=)/i.test(url)) {
    return url;
  }
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}t=${Date.now()}`;
}

export function getAuthToken(auth) {
  if (auth?.token) return auth.token;
  return getStoredAuth().token || "";
}

export function getAuthReferralCode(auth) {
  const source = auth?.token ? auth : getStoredAuth();
  const user = source.user || {};
  return (
    source.referral_code ||
    source.referral ||
    source.code ||
    user.referral_code ||
    user.referral ||
    user.code ||
    ""
  );
}

export function getAuthCredits(auth) {
  const source = auth?.token ? auth : getStoredAuth();
  const user = source.user || {};
  const value =
    source.credits ??
    source.credit ??
    user.credits ??
    user.credit ??
    null;
  if (value == null || value === "") return null;
  return value;
}

export function getAuthLastEvaluation(auth) {
  const source = auth?.token ? auth : getStoredAuth();
  const user = source.user || {};
  return (
    source.last_evaluation ||
    source.lastEvaluation ||
    user.last_evaluation ||
    user.lastEvaluation ||
    ""
  );
}

export function formatAuthDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const PENDING_REF_CODE_KEY = "pending_ref_code";

export function readReferralCodeFromSearch(search) {
  const raw =
    typeof search === "string"
      ? search
      : typeof window !== "undefined"
        ? window.location.search
        : "";
  try {
    const params = new URLSearchParams(raw.startsWith("?") ? raw : `?${raw}`);
    return String(params.get("ref") || params.get("ref_code") || "").trim();
  } catch (e) {
    return "";
  }
}

/** Prefill email from invite links, e.g. /signup/?ref=CODE&email=user@example.com */
export function readSignupEmailFromSearch(search) {
  const raw =
    typeof search === "string"
      ? search
      : typeof window !== "undefined"
        ? window.location.search
        : "";
  try {
    const params = new URLSearchParams(raw.startsWith("?") ? raw : `?${raw}`);
    const email = String(
      params.get("email") || params.get("Email") || ""
    ).trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "";
    return email;
  } catch (e) {
    return "";
  }
}

export function captureReferralCodeFromSearch(search) {
  const code = readReferralCodeFromSearch(search);
  if (!code || typeof window === "undefined") return code;
  try {
    window.sessionStorage.setItem(PENDING_REF_CODE_KEY, code);
  } catch (e) {
    /* ignore storage errors */
  }
  return code;
}

export function getPendingReferralCode(search) {
  const fromUrl = readReferralCodeFromSearch(search);
  if (fromUrl) return fromUrl;
  if (typeof window === "undefined") return "";
  try {
    return String(window.sessionStorage.getItem(PENDING_REF_CODE_KEY) || "").trim();
  } catch (e) {
    return "";
  }
}

export function clearPendingReferralCode() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(PENDING_REF_CODE_KEY);
  } catch (e) {
    /* ignore storage errors */
  }
}

export function buildReferralUrl(auth) {
  const source = auth?.token ? auth : getStoredAuth();
  const user = source.user || {};
  const code =
    getAuthReferralCode(source) ||
    source.mobile_number ||
    source.phone ||
    user.mobile_number ||
    user.phone ||
    source.username ||
    user.username ||
    getAuthUserId(source) ||
    "";
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  if (!code) return origin ? `${origin}/signup` : "/signup";
  return `${origin}/signup?ref=${encodeURIComponent(code)}`;
}
